import React, { useState, useEffect, useRef, useCallback } from 'react';
import { photosService } from '../services/photos.service';
import { Photo } from '../types/photo';
import '../styles/PhotoManager.css';

const MAX_PHOTOS = 8;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

interface PhotoManagerProps {
  entityType: 'coloc' | 'room';
  entityId: string;
}

const PhotoManager: React.FC<PhotoManagerProps> = ({ entityType, entityId }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const loadPhotos = useCallback(async () => {
    try {
      const data = await photosService.list(entityType, entityId);
      setPhotos(data.sort((a, b) => a.position - b.position));
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  // Auto-dismiss error after 5s
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const validateFiles = (files: File[]): File[] => {
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      setError(`Nombre maximum de photos atteint (${MAX_PHOTOS}).`);
      return [];
    }

    const valid: File[] = [];
    for (const file of files) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setError(`"${file.name}" : format non supporté. Formats acceptés : JPEG, PNG, GIF, WebP.`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(`"${file.name}" : taille trop importante (max ${MAX_FILE_SIZE_MB} Mo).`);
        continue;
      }
      valid.push(file);
    }

    if (valid.length > remaining) {
      setError(`Seulement ${remaining} photo(s) restante(s). Les premières seront uploadées.`);
      return valid.slice(0, remaining);
    }

    return valid;
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);

    const validFiles = validateFiles(Array.from(files));
    if (validFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress({ current: 0, total: validFiles.length });

    try {
      for (let i = 0; i < validFiles.length; i++) {
        setUploadProgress({ current: i + 1, total: validFiles.length });
        await photosService.upload(entityType, entityId, validFiles[i]);
      }
      await loadPhotos();
    } catch (err: unknown) {
      if (err && typeof err === "object" && "response" in err) {
        const response = (err as { response: { data?: { detail?: string } } }).response;
        setError(response.data?.detail || "Erreur lors de l'upload.");
      } else {
        setError("Erreur lors de l'upload.");
      }
    } finally {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (photoId: string) => {
    setConfirmDelete(null);
    setDeletingId(photoId);
    try {
      await photosService.delete(photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch {
      setError("Erreur lors de la suppression.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= photos.length) return;

    const newPhotos = [...photos];
    [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
    setPhotos(newPhotos);

    try {
      await photosService.reorder(entityType, entityId, newPhotos.map(p => p.id));
    } catch {
      await loadPhotos();
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const canUpload = photos.length < MAX_PHOTOS && !isUploading;

  if (isLoading) {
    return <div className="photo-manager-loading">Chargement des photos...</div>;
  }

  return (
    <div className="photo-manager">
      {/* Error message */}
      {error && (
        <div className="photo-error">
          {error}
          <button className="photo-error-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Dropzone */}
      <div
        className={`photo-dropzone ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''} ${!canUpload ? 'disabled' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={canUpload ? handleDrop : undefined}
        onClick={() => canUpload && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          onChange={(e) => handleUpload(e.target.files)}
          style={{ display: 'none' }}
        />
        {isUploading ? (
          <div className="upload-progress">
            <div className="upload-spinner" />
            <span className="dropzone-text">
              Upload en cours ({uploadProgress.current}/{uploadProgress.total})...
            </span>
          </div>
        ) : (
          <>
            <span className="dropzone-text">
              {canUpload
                ? 'Glissez des photos ici ou cliquez pour ajouter'
                : `Limite atteinte (${MAX_PHOTOS} photos)`}
            </span>
            <span className="dropzone-info">
              JPEG, PNG, GIF, WebP — max {MAX_FILE_SIZE_MB} Mo par photo
            </span>
          </>
        )}
      </div>

      {/* Photo counter */}
      <div className="photo-counter">
        {photos.length}/{MAX_PHOTOS} photos
      </div>

      {photos.length === 0 ? (
        <p className="photo-manager-empty">Aucune photo</p>
      ) : (
        <div className="photo-grid">
          {photos.map((photo, index) => (
            <div key={photo.id} className={`photo-card ${deletingId === photo.id ? 'deleting' : ''}`}>
              <img
                src={photo.url}
                alt={`Photo ${index + 1}`}
                className="photo-thumbnail"
                onClick={() => setLightboxUrl(photo.url)}
                style={{ cursor: 'zoom-in' }}
              />
              <div className="photo-overlay">
                <div className="photo-actions">
                  {index > 0 && (
                    <button
                      className="photo-action-btn move-btn"
                      onClick={() => handleMove(index, -1)}
                      title="Déplacer à gauche"
                    >
                      ←
                    </button>
                  )}
                  <button
                    className="photo-action-btn delete-btn"
                    onClick={() => setConfirmDelete(photo.id)}
                    disabled={deletingId === photo.id}
                    title="Supprimer"
                  >
                    ✕
                  </button>
                  {index < photos.length - 1 && (
                    <button
                      className="photo-action-btn move-btn"
                      onClick={() => handleMove(index, 1)}
                      title="Déplacer à droite"
                    >
                      →
                    </button>
                  )}
                </div>
              </div>
              <span className="photo-position">{index + 1}</span>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="photo-modal-backdrop" onClick={() => setConfirmDelete(null)}>
          <div className="photo-modal" onClick={(e) => e.stopPropagation()}>
            <p>Supprimer cette photo ?</p>
            <div className="photo-modal-actions">
              <button className="photo-modal-btn cancel" onClick={() => setConfirmDelete(null)}>
                Annuler
              </button>
              <button className="photo-modal-btn confirm" onClick={() => handleDelete(confirmDelete)}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="photo-lightbox" onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="Agrandissement" />
          <button className="lightbox-close" onClick={() => setLightboxUrl(null)}>✕</button>
        </div>
      )}
    </div>
  );
};

export default PhotoManager;
